import { NAMED_RESIDENTS } from "./content.ts";
import type {
  DialogueView,
  NeighborhoodState,
  NamedResidentDefinition,
} from "./types.ts";

export function dialogueForResident(
  state: Readonly<NeighborhoodState>,
  residentId: string,
): DialogueView {
  const resident = NAMED_RESIDENTS.find((item) => item.id === residentId);
  if (resident === undefined) {
    throw new Error(`Unknown named resident ${residentId}`);
  }
  if (residentId === "ona-vaitkute") {
    if (state.quest.status === "not_started") {
      return view(
        resident,
        "Rūta left a brass key in my sugar bowl. I don't take sugar. Either she is subtle or very tired.",
        [["accept_ona_parcel", "Carry it to Rūta."], ["leave", "Not just now."]],
      );
    }
    if (state.quest.outcome === "delivered_sealed") {
      return view(resident, "Rūta sent word. Nothing missing, nothing added. A rare luxury.", []);
    }
    if (state.quest.outcome === "delivered_opened") {
      return view(resident, "You opened the paper. Curiosity is useful, but it charges rent.", []);
    }
    if (state.quest.outcome === "entrusted_to_mantas") {
      return view(resident, "You gave my errand an errand of its own. Very Vilnius.", []);
    }
    return view(resident, "The key is small. The route is the part that takes up space.", []);
  }
  if (residentId === "mantas-simkus" && state.quest.status === "carrying") {
    return view(
      resident,
      state.quest.parcel === "sealed"
        ? "That paper is folded like Ona folded it. I can deliver it by the roof stairs. Probably."
        : "So the paper has already become public architecture. Let me finish the job.",
      [
        ["entrust_mantas", "Trust Mantas with it."],
        ...(state.quest.parcel === "sealed"
          ? [["inspect_parcel", "Open it here."] as const]
          : []),
        ["leave", "Keep carrying it."],
      ],
    );
  }
  if (residentId === "ruta-kalniene" && state.quest.status === "carrying") {
    return view(
      resident,
      state.quest.parcel === "sealed"
        ? "Ona found it. Good. Old keys dislike being the newest thing in a room."
        : "The paper is open. Since the secret survived, bring me the key anyway.",
      [[
        state.quest.parcel === "sealed"
          ? "deliver_ruta_sealed"
          : "deliver_ruta_opened",
        "Give Rūta the key.",
      ]],
    );
  }
  if (residentId === "antanas-valeika") {
    return view(
      resident,
      state.access.blueGate
        ? "Ona says you carry keys without collecting doors. The blue gate is open to you."
        : state.access.rooftopShortcut
          ? "Mantas vouched for you. That is not the same as reassurance. Use the roof stair."
          : "The blue gate is for neighbours and people who return what they borrow.",
      [],
    );
  }
  if (residentId === "ruta-kalniene" && state.access.archiveConversation) {
    return view(resident, "Since you already read the outside, you may as well hear what was left out.", []);
  }
  return view(resident, incidentalLine(resident, state), []);
}

function view(
  resident: NamedResidentDefinition,
  line: string,
  choices: readonly (readonly [string, string])[],
): DialogueView {
  return {
    residentId: resident.id,
    speaker: `${resident.name} · ${resident.role}`,
    line,
    choices: choices.map(([id, label]) => ({ id, label })),
  };
}

function incidentalLine(
  resident: NamedResidentDefinition,
  state: Readonly<NeighborhoodState>,
): string {
  if (state.reputation.reliable > 0 && resident.traits.includes("practical")) {
    return "They say you finish an errand before inventing a second one. Sensible.";
  }
  if (state.reputation.nosy > 0 && resident.traits.includes("curious")) {
    return "You look into things. Sometimes the things look back. Usually it is only a neighbour.";
  }
  if (state.reputation.show_off > 0) {
    return "I heard about the roof route. The pigeons were unimpressed, which is their highest praise.";
  }
  return resident.elderVariant === "none"
    ? "The shortest route is never the one people explain."
    : "You have been walking uphill. That is how the city checks whether you mean it.";
}
